import * as z from 'zod';
import { DEFAULT_PAGE_SIZE, FIRST_PAGE } from '@noor/config';
import { MODERATION_ENTITY_TYPES } from '../lib/moderation-config';

/**
 * Status filter of the moderation queues. `all` sends no `status`; the rest
 * map 1:1 to the five `v1ChangeRequest.status` values the admin queue
 * filters on (`stale` / `withdrawn` are the two closed-without-a-verdict
 * states: the baseline moved on, or the merchant / a newer version replaced
 * the request).
 */
export const MODERATION_STATUS_TABS = [
  'all',
  'pending',
  'approved',
  'rejected',
  'stale',
  'withdrawn',
] as const;
export type ModerationStatusTab = (typeof MODERATION_STATUS_TABS)[number];

/** Request source filter (`v1ChangeRequest.source`); `all` sends none. */
export const MODERATION_SOURCE_FILTERS = ['all', 'manual', 'sync'] as const;
export type ModerationSourceFilter = (typeof MODERATION_SOURCE_FILTERS)[number];

/**
 * URL search-param schema for the four moderation list surfaces. The routes'
 * `validateSearch` uses this, so the URL is the single source of truth.
 *
 * `status` and `source` reach the server (`GET /admin/change-requests`
 * filters on `entity_type` / `status` / `source` / `merchant_id` /
 * `restaurant_id`). `search`, the date range and the product category are
 * URL-only and applied client-side to the fetched page — the endpoint has
 * no text query, date or category filter (the category is matched against
 * the row's `parent`).
 */
export const moderationListSearchSchema = z.object({
  entity: z
    .enum(['restaurants', 'products', 'categories', 'promotions'])
    .catch('products'),
  status: z.enum(MODERATION_STATUS_TABS).catch('all'),
  source: z.enum(MODERATION_SOURCE_FILTERS).catch('all'),
  search: z.string().optional(),
  /** Product category selected for the products queue (client-side). */
  productCategoryId: z.string().optional(),
  /** Inclusive `YYYY-MM-DD` bounds on the request's `created_at`. */
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  page: z.coerce.number().int().positive().default(FIRST_PAGE),
  pageSize: z.coerce.number().int().positive().default(DEFAULT_PAGE_SIZE),
});
export type ModerationListSearch = z.infer<typeof moderationListSearchSchema>;

/** Filter the unified admin queue accepts (`GET /admin/change-requests`). */
export interface ModerationListFilter {
  entityType?: string | undefined;
  status?: string | undefined;
  source?: string | undefined;
  merchantId?: string | undefined;
  restaurantId?: string | undefined;
  limit: number;
  offset: number;
}

/** Map validated URL search → the API filter (pagination → limit/offset). */
export function moderationSearchToFilter(
  search: Pick<ModerationListSearch, 'status' | 'source' | 'page' | 'pageSize'>,
): ModerationListFilter {
  return {
    status: search.status === 'all' ? undefined : search.status,
    source: search.source === 'all' ? undefined : search.source,
    limit: search.pageSize,
    offset: (search.page - 1) * search.pageSize,
  };
}

/** `entity_type` wire value for a queue (sent alongside the filter). */
export function moderationQueueEntityType(
  entityType: keyof typeof MODERATION_ENTITY_TYPES,
): string {
  return MODERATION_ENTITY_TYPES[entityType].queueEntityType;
}
